import type { ApolloError } from "@apollo/client";

/**
 * Symbolic surface of the BE's profile-mutation error codes — every key
 * maps to a deliberately phrased message in the `profile.*` i18n
 * namespace (`errorFieldNotAllowed`, `errorFieldPinned`, `errorThemeLocked`,
 * `errorFeatureDisabled`, or the generic `errorGeneric` fallback).
 *
 * Keep the union in sync with `docs/Channels/Web/WEB_PROFILE_TAB.md`.
 */
export type ProfileErrorKind =
  | "fieldNotAllowed"
  | "fieldPinned"
  | "themeLocked"
  | "featureDisabled"
  /** Per-field validation failure (bad number, ENUM mismatch, malformed
   *  date, etc.) — surface inline on the offending row. */
  | "fieldInvalid"
  | "generic";

/** Extract the symbolic kind from an Apollo error (or any unknown value) so
 *  the caller can render an i18n message without sniffing strings itself. */
export function extractProfileErrorKind(err: unknown): ProfileErrorKind {
  const apollo = err as ApolloError | undefined;
  const codes: string[] = [];
  if (apollo?.graphQLErrors) {
    for (const e of apollo.graphQLErrors) {
      const code = (e.extensions as { code?: unknown } | undefined)?.code;
      if (typeof code === "string") codes.push(code);
    }
  }
  // Some BE shapes also include `message` strings — accept exact matches.
  if (typeof apollo?.message === "string") codes.push(apollo.message);
  for (const c of codes) {
    if (c === "webProfileFieldNotAllowed") return "fieldNotAllowed";
    if (c === "webProfileFieldPinned") return "fieldPinned";
    if (c === "webProfileThemeLocked") return "themeLocked";
    if (c === "webProfileFeatureDisabled") return "featureDisabled";
    if (c === "peopleFields.invalidValue") return "fieldInvalid";
  }
  return "generic";
}

/** When the BE returns `peopleFields.invalidValue`, it usually tags the
 *  offending field on `extensions` (`field` / `key`). Pull whichever it
 *  used so we can surface the error inline on that row. Returns `null` when
 *  the hint isn't present (caller will fall back to the top-level banner). */
export function extractInvalidFieldKey(err: unknown): string | null {
  const apollo = err as ApolloError | undefined;
  if (!apollo?.graphQLErrors) return null;
  for (const e of apollo.graphQLErrors) {
    const ext = e.extensions as { code?: unknown; field?: unknown; key?: unknown } | undefined;
    if (!ext) continue;
    if (ext.code !== "peopleFields.invalidValue") continue;
    if (typeof ext.field === "string") return ext.field;
    if (typeof ext.key === "string") return ext.key;
  }
  return null;
}

/** i18n key (in the `profile.*` namespace) for a given error kind. */
export function profileErrorI18nKey(kind: ProfileErrorKind): string {
  switch (kind) {
    case "fieldNotAllowed":
      return "errorFieldNotAllowed";
    case "fieldPinned":
      return "errorFieldPinned";
    case "themeLocked":
      return "errorThemeLocked";
    case "featureDisabled":
      return "errorFeatureDisabled";
    case "fieldInvalid":
      return "errorFieldInvalid";
    default:
      return "errorGeneric";
  }
}
